/* ==========================================================================
 * aef-recently-viewed.js  —  Wave 1 (dwell-time engagement)
 *
 * Two jobs, both 100% client-side (no backend):
 *   1) TRACK: on a product page, record {handle,id} into localStorage
 *      (dedup, newest-first, capped). Source = <script id="aef-current-product">
 *      injected by sections/main-product.liquid.
 *   2) RENDER: fill any [data-aef-recently-viewed] container with product cards
 *      built from /products/{handle}.js. The "+" button reuses the shared
 *      window.aefCartAdd pipeline (assets/aef-cart-drawer.js) — never POSTs
 *      /cart/add.js directly (anti-regression: single source of truth).
 *
 * localStorage key: 'aef_recently_viewed' =
 *   [{h:handle, i:variantSeedId, t:ts, m:minQty, mm:{id biến thể → min}}, ...]
 * (`mm` có từ 14/9/2026, vòng 48 — xem chú thích ở record().)
 * `t` KHÔNG phải trường trang trí: từ 15/9/2026 (vòng 49) history() vứt mục quá
 * NGAY_SONG và ghi lại danh sách đã dọn — xem chú thích ở NGAY_SONG.
 * The stored id is the PRODUCT id (used as the "For You" recommendation seed).
 * ========================================================================== */
(function () {
  'use strict';

  var KEY = 'aef_recently_viewed';
  var CAP = 20;

  /* 🔴 15/9/2026, vòng 49 — BẢN GHI PHẢI CÓ HẠN DÙNG (phát hiện gan-ket-6).
     `record()` ghi `t: Date.now()` từ ngày đầu, nhưng cho tới hôm nay KHÔNG chỗ
     nào trong kho ĐỌC `t` — một trường được ghi mà không ai đọc. Danh sách vì
     thế chỉ bị bào mòn khi khách xem đủ CAP=20 sản phẩm MỚI; ai xem ít hơn thì
     giữ nguyên vĩnh viễn.
     Khách ghé tháng 4 xem 3 hộp bánh trung thu rồi biến mất; tháng 9 quay lại,
     dãy gắn nhãn "👀 Recently viewed" vẫn bày đúng 3 món của 5 tháng trước. Và
     nó không dừng ở một dãy: sections/aef-for-you.liquid:188-189 lấy
     `history()[0].i` làm MỒI cho /recommendations, nên "✨ For You" cũng đi tìm
     hàng liên quan tới món 5 tháng trước. Cả hai dãy đều tự xưng là tín hiệu tươi.

     Vì sao ĐỂ RIÊNG một con số chứ không mượn `CACHE_TTL` (cũng 30 ngày) của
     assets/zone-detection.js — người đối chiếu gợi ý mượn, mình đi hướng khác:
       · HAI KHÁI NIỆM tình cờ cùng một con số. Bên kia là hạn tin BỘ ĐỆM VÙNG
         GIAO (ZIP → zone, dữ liệu địa lý). Bên này là hạn còn gọi một lượt xem
         là "gần đây" (tín hiệu ý định mua). Buộc chúng vào nhau thì ai rút hạn
         bộ đệm vùng xuống 7 ngày là cắt luôn lịch sử xem của khách, im lặng.
       · KHÔNG "đọc" được thật. `CACHE_TTL` là `const` trong closure của
         zone-detection.js, không phơi ra `window`; file này lại nạp bằng thẻ
         <script> riêng ở sections/aef-recently-viewed.liquid:123. Muốn đọc là
         phải sửa thêm file khác VÀ dựng nhánh dự phòng cho trang chưa nạp xong
         aef-head.js — tức vẫn đẻ ra con số thứ hai, chỉ giấu kỹ hơn.
     Người gác scripts/test-vua-xem-het-han.js ĐỌC thẳng con số dưới đây từ tệp
     nguồn (không chép), nên đổi 30 ngày thành số khác thì nó đi theo, không đỏ oan.
     ⚠️ Đây là hạn của MỘT MÁY. localStorage không đồng bộ giữa điện thoại và máy
     bàn, nên đừng coi nó là "lịch sử xem của khách" ở tầm tài khoản. */
  var NGAY_SONG = 30 * 24 * 60 * 60 * 1000;

  /* Mục thiếu `t` = bản ghi sinh TRƯỚC bản vá này ⇒ coi như CÒN HẠN. Vứt chúng
     đi là mọi khách cũ mất sạch dãy "Vừa xem" ngay lần đẩy đầu — đổi một lỗi
     chậm (hàng ôi) lấy một lỗi nhanh (dãy trống), và không ai kêu để mình biết.
     Chúng tự lành: lần khách xem lại SP đó, record() đóng `t` mới.
     Mốc tương lai (đồng hồ máy khách chạy sai) cho hiệu số âm ⇒ vẫn còn hạn:
     bản ghi kỳ quặc không phải cớ để xoá đồ của khách. */
  function conHan(x) {
    if (!x || !x.h) return false;
    var t = typeof x.t === 'number' ? x.t : 0;
    if (!(t > 0)) return true;
    return Date.now() - t <= NGAY_SONG;
  }

  // ---- storage helpers ----------------------------------------------------
  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function write(arr) {
    try { window.localStorage.setItem(KEY, JSON.stringify(arr.slice(0, CAP))); } catch (e) {}
  }

  // Record the product currently being viewed (newest first, dedup by handle).
  // `min` = custom.min_qty, ghi nhớ NGAY LÚC KHÁCH XEM PDP — đó là chỗ duy nhất
  // biết metafield, vì /products/{handle}.js không trả metafield. Dãy "Vừa xem"
  // dùng lại con số này để nút "+" thêm đủ số lượng tối thiểu, giống mọi nút "+"
  // khác trong theme. Xem chú thích ở sections/aef-recently-viewed.liquid:14.
  //
  /* 🔴 14/9/2026, vòng 48 — MỘT CON SỐ `m` KHÔNG ĐỦ: NÓ LÀ MIN CỦA **MỘT** BIẾN THỂ.
     `m` do máy chủ tính cho biến thể RẺ NHẤT CÒN HÀNG *lúc khách xem PDP*
     (sections/aef-recently-viewed.liquid:71-79). Nhưng `pickVariant()` chọn LẠI
     biến thể rẻ nhất còn hàng *lúc dựng thẻ*, từ /products/{handle}.js — dữ liệu
     MỚI. Bản ghi này không có hạn dùng (chỉ dedup theo handle + cắt CAP 20) nên
     con số cũ nằm lại nhiều ngày, trong khi tồn kho tạp hoá lật mỗi ngày ⇒ hai đầu
     nói về HAI biến thể khác nhau, mà min lại tính theo BẬC GIÁ của biến thể:
       acecook-thailand-hotpot-noodles-2-9oz — xem lúc "1 each" $1,19 còn hàng ⇒
       m=4; hôm sau chỉ còn "pack of 30" $28,19 ⇒ thẻ in "Min order: 4 · $112.76"
       và nút "+" nạp $112,76 thay vì $28,19 (dư $84,57 MỖI CÚ BẤM, đúng 4,00 lần).
       Chiều ngược lại im lặng hơn: xem lúc chỉ còn pack ⇒ m=1; hôm sau hàng lẻ về
       ⇒ nút "+" nạp đúng 1 × $1,19, dưới bậc 4 mà Boss chốt 17/8 để khỏi lỗ.
     Đo trên bản sao kho active (2.699 SP): 301 SP (11,2%) có giá min và max rơi
     KHÁC BẬC ⇒ nằm trong tầm khi tồn kho lật.
     Máy chủ ĐÃ chở sẵn bản đồ {id biến thể → min} trong khối JSON
     (sections/aef-recently-viewed.liquid:82-88, in ra mỗi lượt PDP từ vòng 42) —
     chỉ chưa ai tra, vứt đi 100% số lần. Nay cất nó cùng bản ghi dưới tên `mm`.
     ⛔ KHÔNG chép bảng bậc giá sang JS: đã có 2 bản (snippets/aef-min-qty.liquid
     + `tier()` của aef-moq-relax.js), bản thứ ba là mở đường cho chúng trôi xa
     nhau — scripts/test-min-qty-mot-nguon.js canh đúng chuyện đó.
     Chỉ giữ cặp có min > 1: vắng mặt ⇒ 1, đúng nhánh sẵn có của buildCard, nên
     lược `:1` không đổi hành vi mà bản ghi khỏi phồng (SP nhiều biến thể × 20 chỗ).
     ⚠️ Bản vá này THU HẸP khe hở chứ KHÔNG bịt kín: nó chữa trục "đổi BIẾN THỂ",
     không chữa trục "đổi GIÁ". `mm` cũng là ảnh chụp lúc xem PDP, nên Boss bật/tắt
     sale vượt mốc 120/220/350 xu là bậc đổi mà `mm` vẫn nói số cũ — kể cả SP chỉ
     có MỘT biến thể (1.382/1.500 SP đo được). Đo 14/9: 63 dòng đang giảm giá, 1
     dòng vượt ranh giới bậc (vion-hue-style-beef-instant-noodles-2-3oz $3,00→$1,60,
     bậc 2→3). Đừng để phiên sau tưởng đã kín.
     Người gác: scripts/test-vua-xem-min-troi-ton-kho.js */
  function locMins(mins) {
    if (!mins || typeof mins !== 'object') return null;
    var out = {};
    var ks = Object.keys(mins);
    for (var i = 0; i < ks.length; i++) {
      var n = parseInt(mins[ks[i]], 10);
      if (n > 1) out[ks[i]] = n;
    }
    return out;
  }

  function record(handle, id, min, mins) {
    if (!handle) return;
    var arr = read().filter(function (x) { return x && x.h !== handle; });
    var m = parseInt(min, 10);
    var rec = { h: handle, i: id || 0, t: Date.now(), m: (m > 1 ? m : 1) };
    // `mm` CÓ MẶT (kể cả rỗng) = bản ghi sinh SAU bản vá này ⇒ tin bản đồ, id nào
    // vắng thì id đó min = 1. `mm` VẮNG = bản ghi khách đã có TRƯỚC bản vá ⇒ giữ
    // đường lùi, vẫn dùng `m` y như cũ, và tự lành ngay lần khách xem lại SP đó.
    var mm = locMins(mins);
    if (mm) rec.mm = mm;
    arr.unshift(rec);
    write(arr);
  }

  /* Vứt hàng ôi khỏi CHÍNH localStorage rồi trả về phần còn hạn — dọn thật, không
     chỉ lọc lúc đọc. Hai lý do phải ghi xuống kho chứ không lọc suông:
       · hàng ôi còn nằm đó là còn chiếm chỗ trong CAP = 20, đẩy hàng tươi ra;
       · và nó còn một NGƯỜI ĐỌC THỨ HAI không đi qua history():
         sections/aef-for-you.liquid:192 đọc thẳng `localStorage['aef_recently_viewed']`
         làm đường lùi cho lúc `window.aefRV` chưa kịp có. Đường đó không hưởng
         được bộ lọc nào ở đây. Bịt bằng DỮ LIỆU — dọn kho ngay lúc tệp này chạy
         (xem init()) — chứ đừng chép luật lọc sang file thứ hai: một khái niệm mà
         hai nơi tự định nghĩa là họ lỗi hay gặp nhất ở kho này.
     🔴 Nó lọc THEO TUỔI, và CHỈ theo tuổi. Đừng bao giờ ghi xuống kho một danh
     sách đã trừ `excludeHandle`: handle đó là SP khách ĐANG XEM, record() vừa đóng
     vào lịch sử vài mili-giây trước — ghi đè bằng danh sách đã trừ nó là xoá ngay
     thứ vừa ghi, và dãy "Vừa xem" thành cái nhớ mọi món TRỪ món gần nhất. */
  function don() {
    var arr = read();
    var song = arr.filter(conHan);
    if (song.length !== arr.length) write(song);
    return song;
  }

  // Public: newest-first history, optionally excluding one handle (the PDP you're on).
  function history(excludeHandle) {
    return don().filter(function (x) {
      return !excludeHandle || x.h !== excludeHandle;
    });
  }

  // ---- money format (USD store) -------------------------------------------
  // 23/10/2026 (GG1) — tiền đi qua MỘT hàm window.aefTien (layout/theme.liquid, <head>);
  // 30/10/2026 (/code-review 287d9d08 #11): đã gỡ dự phòng tự nối dấu đô — section này chỉ dựng dưới
  // layout/theme.liquid, aefTien có sẵn từ <head>. Người gác: scripts/test-tien-mot-dinh-dang.js CA 6
  function money(cents) {
    if (typeof cents !== 'number') return '';
    return window.aefTien(cents);
  }

  // ---- card builder -------------------------------------------------------
  // Biến thể mà CẢ THẺ phải đại diện: giá, ảnh, link ?variant=, và nút "+".
  //
  // 2026-08-16 — SỬA LỖI NÓI MỘT ĐẰNG LÀM MỘT NẺO. Trước đây hàm này trả về biến
  // thể CÒN HÀNG ĐẦU TIÊN theo thứ tự sắp xếp, còn giá lại lấy p.price (= giá
  // THẤP NHẤT của sản phẩm). Hai con số khác nhau, và ai cũng tin con số nhìn
  // thấy. Đo trên hàng thật (250 SP đầu, 16/8): 3 sản phẩm đang lệch, nặng nhất
  //   lee-kum-kee-premium-oyster-sauce-18-oz — thẻ ghi $9,99, nút "+" thêm biến
  //   thể 32 oz giá $20,99 (gấp 2,1 lần).
  //   indomie-chicken-curry… $1,09 → $4,99 · chinsu-chili-sauce-250g $4,49 → $5,99.
  //
  // Luật đúng đã có sẵn trong theme, không được chế luật thứ hai: xem
  // snippets/product-card.liquid:28-79 (card_default_variant) —
  //   1) biến thể bestseller nếu còn hàng  2) RẺ NHẤT CÒN HÀNG  3) biến thể đầu.
  // Bước 1 không áp dụng được ở đây: /products/{handle}.js không trả metafield,
  // và dãy "Vừa xem" không phải ngữ cảnh bestseller. Nên lấy bước 2 và 3.
  //
  // Chính chú thích ở product-card.liquid:364 đã cảnh báo đúng cái bẫy này:
  // "first_available_variant.price would surface those (e.g. $59.99 pack)
  //  instead of the single-unit min price ($5.99)".
  function pickVariant(p) {
    var vs = (p && p.variants) || [];
    var best = null;
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i];
      if (!v || !v.available) continue;
      if (!best || v.price < best.price) best = v;   // RẺ NHẤT còn hàng, không phải đầu tiên
    }
    return best || vs[0] || null;                    // hết hàng cả loạt → biến thể đầu (không có nút "+")
  }

  // `minQty` = custom.min_qty đã ghi nhớ lúc khách xem PDP (xem record()).
  // Không có (món lưu từ trước bản vá) → 1, và tự lành ngay lần khách xem lại SP đó.
  // `mins` (14/9/2026, vòng 48) = bản đồ {id biến thể → min} của CÙNG lượt chụp đó.
  // Có bản đồ thì nó THẮNG, vì nó biết đúng biến thể `pickVariant()` vừa chọn —
  // `minQty` là một con số duy nhất, chụp cho biến thể còn hàng NGÀY HÔM ĐÓ.
  function buildCard(p, minQty, mins) {
    var v = pickVariant(p);
    var minN = mins
      ? parseInt(mins[v && v.id ? v.id : ''], 10)   // vắng trong bản đồ = bậc 1 (record() lược `:1`)
      : parseInt(minQty, 10);                        // đường lùi cho bản ghi trước vòng 48
    if (!(minN > 1)) minN = 1;
    var a = document.createElement('a');
    a.className = 'aef-rv-card';
    // Link phải mở ĐÚNG biến thể thẻ đang chào, nếu không PDP lại tự chọn cái
    // khác — vẫn là Pattern 4, chỉ chậm hơn một nhịp.
    a.href = '/products/' + p.handle + (v && v.id ? '?variant=' + v.id : '');
    a.setAttribute('aria-label', p.title || '');

    var media = document.createElement('div');
    media.className = 'aef-rv-card__media';
    // Ảnh cũng theo biến thể đó khi biến thể có ảnh riêng (v.featured_image là
    // OBJECT có .src; p.featured_image là CHUỖI // giao thức tương đối).
    var vImg = v && v.featured_image && v.featured_image.src;
    var imgSrc = vImg || p.featured_image || (p.images && p.images[0]) || '';
    if (imgSrc) {
      var img = document.createElement('img');
      // request a sized thumbnail from Shopify CDN
      img.src = String(imgSrc).replace(/(\.[a-zA-Z]+)(\?|$)/, '_360x$1$2');
      img.alt = p.title || '';
      img.loading = 'lazy';
      img.width = 360; img.height = 360;
      media.appendChild(img);
    } else {
      media.innerHTML = '<span class="aef-rv-card__ph" aria-hidden="true">🛒</span>';
    }

    var body = document.createElement('div');
    body.className = 'aef-rv-card__body';

    var title = document.createElement('h3');
    title.className = 'aef-rv-card__title';
    title.textContent = p.title || '';

    var row = document.createElement('div');
    row.className = 'aef-rv-card__row';

    var price = document.createElement('span');
    price.className = 'aef-rv-card__price';
    // Giá của ĐÚNG biến thể nút "+" sẽ thêm — không phải p.price (giá thấp nhất
    // của sản phẩm), vì hai thứ đó lệch nhau ở 3 SP đo được. Chỉ khi không có
    // biến thể nào mới rơi về giá thấp nhất.
    var giaXu = v && typeof v.price === 'number' ? v.price
      : (typeof p.price === 'number' ? p.price : (p.price_min || 0));
    price.textContent = money(giaXu);

    row.appendChild(price);

    // Nói ra số lượng tối thiểu — thẻ thường có dòng này (product-card.liquid:385),
    // dãy "Vừa xem" thì không, nên khách không hiểu vì sao bấm một cái vào giỏ ba.
    // 01/11/2026: dòng này gắn SAU tên (cuối hàm). Trước đó nó đứng trước tên nên
    // đẩy tên xuống 29px so với thẻ kề bên.
    /* Thẻ hết hàng không có nút thêm giỏ nên không có gì để nói về số lượng tối thiểu, cùng luật dòng Min của PDP. */
    var hint = null;
    if (minN > 1 && v && v.available) {
      hint = document.createElement('span');
      hint.className = 'card__min-hint aef-rv-card__min';
      var hintLabel = document.createElement('span');
      hintLabel.setAttribute('data-i18n', 'card.min_hint');
      hintLabel.textContent = 'Min order:';
      hint.appendChild(hintLabel);
      /* 18/8 — kèm thành tiền, dùng ĐÚNG `giaXu` mà dòng giá ngay trên vừa in ra
         (HARD GATE Pattern 4: hai con số trên cùng một thẻ phải cùng một biến thể).
         Số nằm NGOÀI span data-i18n (BUG-J). */
      hint.appendChild(document.createTextNode(' ' + minN + ' · ' + money(giaXu * minN)));
    }

    // "+" quick-add — reuses the shared cart pipeline
    if (v && v.available) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'aef-rv-card__add';
      btn.setAttribute('aria-label', 'Add to cart');
      btn.setAttribute('data-i18n-aria-label', 'card.add_to_cart');
      // Hai phần tử con thay cho một chữ "+" trần, để CSS chọn hiện cái nào:
      //   · trang chủ  -> ẩn "+", hiện nhãn chữ (nút thanh ngang kiểu Harris Farm)
      //   · trang khác -> ẩn nhãn chữ, hiện "+" (vòng tròn 36px như cũ)
      // Không đổi hành vi: vẫn đúng một nút, vẫn gọi window.aefCartAdd.
      // Nhãn dùng khoá `card.add_to_cart` có sẵn; thẻ này do JS tiêm sau khi
      // applyTranslations chạy lần đầu nên phải nhờ sự kiện
      // 'aef:cards-injected' ở cuối file dịch lại — sự kiện đó đã có.
      btn.textContent = '';
      var btnPlus = document.createElement('span');
      btnPlus.className = 'aef-rv-card__add-plus';
      btnPlus.setAttribute('aria-hidden', 'true');
      btnPlus.textContent = '+';
      var btnText = document.createElement('span');
      btnText.className = 'aef-rv-card__add-text';
      btnText.setAttribute('data-i18n', 'card.add_to_cart');
      btnText.textContent = 'Add to cart';
      btn.appendChild(btnPlus);
      btn.appendChild(btnText);
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window.aefCartAdd === 'function') {
          // Thêm ĐỦ số lượng tối thiểu. Trước đây ghi cứng 1, nên khách xem
          // Morton Iodized Salt (Min order: 3) rồi bấm "+" ở dãy "Vừa xem" chỉ
          // vào giỏ 1 — dưới MOQ, trong khi mọi nút "+" khác trong theme đều
          // thêm đủ (thẻ thường và PDP đọc data-min-qty do máy chủ vẽ).
          /* 2026-08-17 — trước đây hai dòng dưới chạy NGAY sau lời gọi:
               btn.classList.add('is-added');
               setTimeout(… remove …, 900);
             tức dán ✓ ở mốc 0ms, không đọc kết quả trả về, không khoá nút.
             Bấm hai lần là hai món thật (hàng đợi nối tiếp, không mất request
             nào). Nay khoá nút + chỉ dán ✓ khi máy chủ đã nhận.
             `aefCartAdd` resolve ra true/false chứ KHÔNG throw, nên đọc giá
             trị chứ đừng bắt lỗi — xem aef-cart-drawer.js:389. */
          if (window.aefBtnLock && !window.aefBtnLock(btn)) return;
          Promise.resolve(window.aefCartAdd(v.id, minN, btn)).then(function (ok) {
            if (window.aefBtnUnlock) window.aefBtnUnlock(btn, ok !== false);
          }, function () {
            if (window.aefBtnUnlock) window.aefBtnUnlock(btn, false);
          });
        }
      });
      row.appendChild(btn);
    } else if (v) {
      /* 01/11/2026: thẻ hết hàng từng chỉ còn con số giá, không nhãn, không nút, và giá tụt
         50px vì hàng thiếu chiều cao nút. Ô này đứng đúng chỗ nút nên giá thẳng hàng; nó là
         span, cú bấm rơi xuống link của thẻ. Chữ qua data-i18n như nút thêm giỏ. */
      var het = document.createElement('span');
      het.className = 'aef-rv-card__het';
      het.setAttribute('data-i18n', 'card.sold_out');
      het.textContent = 'Sold out';
      row.appendChild(het);
    }

    body.appendChild(title);
    if (hint) body.appendChild(hint);
    body.appendChild(row);
    a.appendChild(media);
    a.appendChild(body);
    return a;
  }

  // ---- render one container -----------------------------------------------
  /* 🔴 29/8/2026 — "ĐÃ DỰNG" KHÁC "ĐÃ THỬ DỰNG".
     Dấu `data-rv-done` trước đây được đóng NGAY TRƯỚC khi gọi fetch, chứ không
     phải sau khi dựng xong. Hai hỏng chồng lên nhau:
       · một nhịp mạng hỏng (429 / mất sóng / 5xx) ⇒ `shown === 0` ⇒ dãy tự ẩn;
       · và chính cái dấu vừa đóng chặn mọi lần chạy sau ⇒ không có đường quay lại
         trong cả lượt xem đó.
     Đo thật 29/8 trên trang chủ staging: Shopify trả 429 cho CẢ BỐN request
     /products/*.js, dãy "Vừa xem" im lặng biến mất, `data-rv-done="1"`. Khách đi
     mạng di động chập chờn gặp đúng cảnh này — mà không kêu một tiếng.
     Nay: dấu chỉ đóng KHI ĐÃ DỰNG ĐƯỢC THẺ. Hỏng thoáng qua thì thử lại (tối đa
     LAN_THU lần, giãn dần). 404/410 KHÔNG tính là hỏng thoáng qua — đó là hàng
     chết, đã có nhánh riêng gỡ khỏi localStorage.
     Người gác: scripts/test-vua-xem-thu-lai-khi-mang-hong.js */
  var LAN_THU = 3;         /* tổng số lần thử cho một khung */
  var GIAN = [900, 2600];  /* giãn cách giữa các lần, ms */

  function renderInto(container, lan) {
    lan = lan || 0;
    if (!container || container.getAttribute('data-rv-done') === '1') return;
    if (container.getAttribute('data-rv-dang') === '1') return;   /* đang chạy dở */
    var exclude = container.getAttribute('data-exclude-handle') || '';
    var limit = parseInt(container.getAttribute('data-limit'), 10) || 12;
    var items = history(exclude).slice(0, limit);
    var section = container.closest('[data-aef-rv-section]') || container.parentElement;

    if (!items.length) {
      if (section) section.setAttribute('hidden', 'hidden');
      container.setAttribute('data-rv-done', '1');   /* không có lịch sử: hết việc thật */
      return;
    }
    container.setAttribute('data-rv-dang', '1');

    // handle → min_qty đã ghi nhớ, để buildCard biết SP nào bắt buộc mua tối thiểu.
    // Kèm handle → bản đồ {id biến thể → min} (vòng 48) cho bản ghi nào có `mm`.
    var minByHandle = {};
    var minsByHandle = {};
    items.forEach(function (it) {
      if (!it || !it.h) return;
      minByHandle[it.h] = it.m;
      if (it.mm && typeof it.mm === 'object') minsByHandle[it.h] = it.mm;
    });

    /* 🔴 24/8/2026 — HANDLE CHẾT PHẢI BỊ DỌN, KHÔNG PHẢI LẶNG LẼ BỎ QUA.
       Đổi handle sản phẩm thì Shopify tạo chuyển hướng 301 — nhưng CHỈ trên
       đường dẫn HTML. Endpoint Ajax `.js` trả 404 THẲNG. Đo thật 24/8 sau khi
       đổi 14 handle:
         /products/mix-hot-chilli-biscuit-sticks-400g-copy.js  → 404, 0 byte
         /products/lotus-biscuit-stick-…-50g.js                → 200, 4.948 byte
         /products/mix-hot-chilli-…-copy?variant=…             → 301 (HTML thì ổn)
       Bản cũ nuốt 404 thành `null` rồi `if (!p) return` ⇒ hai hỏng chồng nhau:
       thẻ biến mất không kêu một tiếng, VÀ handle chết chiếm vĩnh viễn 1 trong
       20 chỗ của dãy cho tới khi bị 20 lượt xem mới đẩy ra.
       Nay: 404/410 ⇒ GỠ handle đó khỏi localStorage ngay. Lỗi mạng thoáng qua
       (catch) thì KHÔNG gỡ — mất mạng một nhịp không phải bằng chứng SP đã chết. */
    var chet = [];
    var thoangQua = 0;   /* hỏng có thể tự lành: 429, 5xx, đứt mạng */
    Promise.all(items.map(function (it) {
      return fetch('/products/' + it.h + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (r.ok) return r.json();
          if (r.status === 404 || r.status === 410) { chet.push(it.h); return null; }
          thoangQua++;
          return null;
        })
        .catch(function () { thoangQua++; return null; });
    })).then(function (products) {
      container.removeAttribute('data-rv-dang');
      if (chet.length) {
        write(read().filter(function (x) { return x && chet.indexOf(x.h) === -1; }));
      }
      var frag = document.createDocumentFragment();
      var shown = 0;
      products.forEach(function (p) {
        if (!p || !p.handle) return;
        frag.appendChild(buildCard(p, minByHandle[p.handle], minsByHandle[p.handle]));
        shown++;
      });
      if (!shown) {
        if (section) section.setAttribute('hidden', 'hidden');
        /* Còn cửa: hỏng thoáng qua thì thử lại, ĐỪNG đóng dấu "xong". Hết lượt
           thử, hoặc không có ai hỏng thoáng qua (mọi handle đều chết thật) thì
           mới chốt — chốt sớm là mất dãy cả lượt xem. */
        if (thoangQua && lan + 1 < LAN_THU) {
          setTimeout(function () { renderInto(container, lan + 1); },
            GIAN[Math.min(lan, GIAN.length - 1)]);
        } else {
          container.setAttribute('data-rv-done', '1');
        }
        return;
      }
      container.setAttribute('data-rv-done', '1');
      container.innerHTML = '';
      container.appendChild(frag);
      if (section) section.removeAttribute('hidden');
      // re-localize the freshly injected [data-i18n-*] nodes if the i18n engine is present
      try {
        // Xem chú thích trong sections/aef-for-you.liquid: window.aefApplyI18n
        // không tồn tại ở bất kỳ đâu và 'aef:i18n:apply' không ai nghe. Sự kiện
        // đúng là 'aef:cards-injected' — ae-delivery-widget.js dịch lại, và
        // card-stepper.js dựng lại số lượng theo giỏ cho thẻ vừa tiêm.
        document.dispatchEvent(new CustomEvent('aef:cards-injected', { detail: { sectionId: 'aef-recently-viewed' } }));
      } catch (e) {}
    });
  }

  // Defer render to idle time. We must NOT gate on IntersectionObserver here:
  // the section starts [hidden] (display:none) and a display:none element never
  // reports as intersecting, which would deadlock (render waits for visibility,
  // visibility waits for render). History is known synchronously from
  // localStorage, so renderInto() decides reveal-or-hide on its own.
  function schedule(container) {
    var go = function () { renderInto(container); };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(go, { timeout: 1500 });
    } else {
      setTimeout(go, 300);
    }
  }

  function init() {
    // 0) Dọn hàng quá hạn NGAY, đừng đợi ai gọi history(). Tệp này nạp bằng
    //    <script defer> nên chạy trước DOMContentLoaded, tức trước lượt đọc thẳng
    //    localStorage của sections/aef-for-you.liquid (chạy ở thời gian rảnh) —
    //    tới lượt nó thì kho đã sạch. Xem chú thích ở don().
    don();
    // 1) track current product view
    var meta = document.getElementById('aef-current-product');
    if (meta) {
      try {
        var data = JSON.parse(meta.textContent || '{}');
        // `data.mins` (vòng 48) — bản đồ min theo TỪNG biến thể; trước đây khối JSON
        // in nó ra mỗi lượt PDP mà không ai đọc. Xem record().
        if (data && data.handle) record(data.handle, data.id, data.min, data.mins);
      } catch (e) {}
    }
    // 2) render any recently-viewed containers on the page
    var containers = document.querySelectorAll('[data-aef-recently-viewed]');
    for (var i = 0; i < containers.length; i++) schedule(containers[i]);
  }

  // expose minimal API (For You section reads history() to seed recommendations)
  window.aefRV = { history: history, record: record };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
